/**
 * Solar and lunar position calculations using the Meeus algorithm.
 * Pure math module — no THREE.js dependency.
 *
 * Reference: Jean Meeus, "Astronomical Algorithms", 2nd ed.
 * Accuracy: ±0.01° for dates within ±50 years of J2000.
 * Runtime: <0.01 ms per call.
 *
 * Architecture: environment-taxonomy.md §3.2, weather-domain.md §1
 */

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

export interface SolarPosition {
  /** Degrees from north, clockwise (0 = North, 90 = East) */
  azimuth: number;
  /** Degrees above horizon (negative = below) */
  elevation: number;
}

function julianDay(date: Date): number {
  return date.getTime() / 86_400_000 + 2_440_587.5;
}

function julianCenturies(jd: number): number {
  return (jd - 2_451_545.0) / 36_525;
}

function normalizeAngle(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Compute solar position using the Meeus algorithm (ch. 25 + ch. 13).
 */
export function computeSolarPosition(date: Date, latitude: number, longitude: number): SolarPosition {
  const JD = julianDay(date);
  const T = julianCenturies(JD);
  const lat = latitude * DEG;

  const L0 = normalizeAngle(280.46646 + 36_000.76983 * T);
  const M = normalizeAngle(357.52911 + 35_999.05029 * T);
  const C =
    (1.914602 - 0.004817 * T) * Math.sin(M * DEG) +
    0.019993 * Math.sin(2 * M * DEG) +
    0.000289 * Math.sin(3 * M * DEG);

  const Theta = (L0 + C) * DEG;
  const epsilon = (23.439291 - 0.013004 * T) * DEG;

  const RA = Math.atan2(Math.cos(epsilon) * Math.sin(Theta), Math.cos(Theta));
  const decl = Math.asin(Math.sin(epsilon) * Math.sin(Theta));

  const GMST = normalizeAngle(280.46061837 + 360.98564736629 * (JD - 2_451_545)) * DEG;
  const LST = GMST + longitude * DEG;
  const H = LST - RA;

  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const sinDecl = Math.sin(decl);
  const cosDecl = Math.cos(decl);

  const elev = Math.asin(sinLat * sinDecl + cosLat * cosDecl * Math.cos(H));
  let az = Math.atan2(
    -cosDecl * Math.sin(H),
    sinLat * cosDecl * Math.cos(H) - cosLat * sinDecl,
  );
  az = normalizeAngle(az * RAD);

  return { azimuth: az, elevation: elev * RAD };
}

/**
 * Compute lunar position using simplified Meeus (ch. 47, low-precision).
 * Accuracy: ±0.3° — sufficient for directional lighting.
 */
export function computeLunarPosition(date: Date, latitude: number, longitude: number): SolarPosition {
  const JD = julianDay(date);
  const T = julianCenturies(JD);
  const lat = latitude * DEG;

  const Lm = normalizeAngle(218.3165 + 481_267.8813 * T);
  const D = normalizeAngle(297.8502 + 445_267.1115 * T);
  const Mm = normalizeAngle(134.9634 + 477_198.8676 * T);
  const F = normalizeAngle(93.2721 + 483_202.0175 * T);
  const sunM = normalizeAngle(357.52911 + 35_999.050 * T);

  const lonMoon =
    Lm +
    6.289 * Math.sin(Mm * DEG) -
    1.274 * Math.sin((2 * D - Mm) * DEG) +
    0.658 * Math.sin(2 * D * DEG) -
    0.186 * Math.sin(sunM * DEG) -
    0.059 * Math.sin((2 * Mm - 2 * D) * DEG);

  const latMoon =
    5.128 * Math.sin(F * DEG) +
    0.281 * Math.sin((Mm + F) * DEG) -
    0.280 * Math.sin((F - Mm) * DEG);

  const epsilon = 23.4393 - 0.01300 * T;

  const RA = Math.atan2(
    Math.cos(epsilon * DEG) * Math.sin(lonMoon * DEG) -
      Math.tan(latMoon * DEG) * Math.sin(epsilon * DEG),
    Math.cos(lonMoon * DEG),
  );
  const decl = Math.asin(
    Math.sin(latMoon * DEG) * Math.cos(epsilon * DEG) +
      Math.cos(latMoon * DEG) * Math.sin(epsilon * DEG) * Math.sin(lonMoon * DEG),
  );

  const GMST = normalizeAngle(280.46061837 + 360.98564736629 * (JD - 2_451_545)) * DEG;
  const H = GMST + longitude * DEG - RA;

  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const sinDecl = Math.sin(decl);
  const cosDecl = Math.cos(decl);

  const elev = Math.asin(sinLat * sinDecl + cosLat * cosDecl * Math.cos(H));
  let az = Math.atan2(
    -cosDecl * Math.sin(H),
    sinLat * cosDecl * Math.cos(H) - cosLat * sinDecl,
  );
  az = normalizeAngle(az * RAD);

  return { azimuth: az, elevation: elev * RAD };
}

/**
 * Convert azimuth/elevation to a directional light position vector.
 * Uses Giro3D scene coordinate system: X=East, Y=Up, Z=-North.
 */
export function solarPositionToDirectionalLightPosition(
  solar: SolarPosition,
  distance: number = 1000,
): { x: number; y: number; z: number } {
  const az = solar.azimuth * DEG;
  const el = solar.elevation * DEG;

  const x = Math.sin(az) * Math.cos(el);
  const y = Math.sin(el);
  const z = -Math.cos(az) * Math.cos(el);

  const len = Math.sqrt(x * x + y * y + z * z) || 1;
  return {
    x: (x / len) * distance,
    y: (y / len) * distance,
    z: (z / len) * distance,
  };
}

/** True when elevation ≤ -6° (civil twilight threshold). */
export function isCivilTwilight(elevation: number): boolean {
  return elevation <= -6;
}

/** True when elevation ≤ -12° (nautical twilight threshold). */
export function isNauticalTwilight(elevation: number): boolean {
  return elevation <= -12;
}

/**
 * Compute moon phase as fraction 0..1 (0=new, 0.5=full, 1=new again).
 */
export function computeMoonPhase(date: Date): number {
  const JD = julianDay(date);
  const synodicPeriod = 29.53058867;
  const knownNewMoon = 2_451_550.1; // Jan 6, 2000
  return (((JD - knownNewMoon) % synodicPeriod) + synodicPeriod) % synodicPeriod / synodicPeriod;
}
